import React, { useMemo } from "react"
import { Pencil } from "lucide-react"
import { UNIT_STATUS_STYLES } from "./UnitStatusBadge"

// A clickable, color-coded grid of units. Units are grouped by tower (units
// with no tower fall under "Unassigned"). Clicking a cell opens the status
// dialog via onUnitClick. The pencil on a tower header opens onEditTower.
export function AvailabilityGrid({ units, towers = [], onUnitClick, onEditTower }) {
  const groups = useMemo(() => {
    const towerById = (id) => towers.find((t) => t.id === id)
    const towerName = (id) => towerById(id)?.name
    const byTower = new Map()
    for (const u of units) {
      const key = u.tower_id ?? "none"
      if (!byTower.has(key)) byTower.set(key, [])
      byTower.get(key).push(u)
    }
    return Array.from(byTower.entries())
      .map(([key, list]) => ({
        key,
        tower: key === "none" ? null : towerById(key),
        name: key === "none" ? "Unassigned" : (towerName(key) || `Tower ${key}`),
        units: [...list].sort((a, b) =>
          (a.floor_number ?? 0) - (b.floor_number ?? 0) ||
          String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true })
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [units, towers])

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {Object.entries(UNIT_STATUS_STYLES).map(([status, style]) => (
          <span key={status} className="flex items-center gap-1.5 text-[12px] text-slate-600">
            <span className="h-3 w-3 rounded" style={{ backgroundColor: style.swatchColor }} />
            {style.label}
          </span>
        ))}
      </div>

      {groups.map((group) => (
        <div key={group.key}>
          <div className="mb-2 flex items-center gap-1.5">
            <h4 className="text-[13px] font-semibold text-slate-700">{group.name}</h4>
            {group.tower && onEditTower && (
              <button
                type="button"
                onClick={() => onEditTower(group.tower)}
                title="Edit tower"
                className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-2">
            {group.units.map((unit) => {
              const style = UNIT_STATUS_STYLES[unit.status] || UNIT_STATUS_STYLES.available
              return (
                <button
                  key={unit.id}
                  type="button"
                  onClick={() => onUnitClick(unit)}
                  title={`${unit.unit_number} · ${style.label}`}
                  className="flex aspect-square flex-col items-center justify-center rounded-lg border text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                  style={style.cellStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = style.cellHover }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = style.cellStyle.backgroundColor }}
                >
                  <span className="truncate px-1 text-[12px] font-semibold leading-tight">
                    {unit.unit_number}
                  </span>
                  {unit.unit_type && (
                    <span className="truncate px-1 text-[10px] opacity-70">{unit.unit_type}</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
